import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Award,
  Briefcase,
  Calendar,
  Download,
  ExternalLink,
  FileText,
  Globe,
  Mail,
  MapPin,
  MessageSquare,
  Phone,
  Shield,
  Star,
  ThumbsDown,
  ThumbsUp,
  User,
  X
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { Link, useLocation, useParams } from 'react-router-dom';
import api from '../../utils/api';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      when: 'beforeChildren',
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { type: 'spring', stiffness: 300, damping: 24 },
  },
};

const CandidateDetail = () => {
  const { id } = useParams();
  const locationHook = useLocation();
  const showDecisionButtons = locationHook.state?.fromApplications || false;
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('profile');
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [newNote, setNewNote] = useState('');
  
  // Allowed sequential statuses
  const STATUS_FLOW = ['Reviewed','Shortlisted','Interview Scheduled','Interviewed', 'Selected', 'Offered', 'Offer Accepted', 'Hired', 'Onboarded'];

  // Color map for all statuses
  const getStatusColor = (status) => {
    switch (status) {
      case 'Applied':
      case 'New':
        return 'bg-primary-100 text-primary-700';
      case 'Under Review':
      case 'Reviewed':
        return 'bg-accent-100 text-accent-700';
      case 'Interview Scheduled':
      case 'Interviewed':
      case 'Shortlisted':
        return 'bg-warning-100 text-warning-700';
      case 'Selected':
        return 'bg-info-100 text-info-700';
      case 'Offered':
        return 'bg-indigo-100 text-indigo-700';
      case 'Offer Accepted':
        return 'bg-purple-100 text-purple-700';
      case 'Hired':
        return 'bg-success-100 text-success-700';
      case 'Onboarded':
        return 'bg-teal-100 text-teal-700';
      case 'Rejected':
      case 'Withdrawn':
        return 'bg-secondary-100 text-secondary-700';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // Helper to get next status in flow
  const getNextStatus = (current) => {
    const idx = STATUS_FLOW.indexOf(current);
    if (idx === -1) return 'Reviewed'; // Start the flow if not yet in it
    if (idx === STATUS_FLOW.length - 1) return null; // Already at final step
    return STATUS_FLOW[idx + 1];
  };

  // Fetch candidate data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);

      try {
        const res = await api.get(`/candidates/${id}`);
        const data = res.data?.data;

        if (data) {
          const mapped = mapCandidate(data);

          // If routed from Applications list with applicationId, fetch application to get real status/history
          const applicationIdFromState = locationHook.state?.applicationId;
          if (applicationIdFromState) {
            try {
              const appRes = await api.get(`/applications/${applicationIdFromState}`);
              const appData = appRes.data?.data;
              if (appData) {
                mapped.status = appData.status;
                mapped.statusHistory = appData.statusHistory || [];
                mapped.appliedDate = new Date(appData.appliedAt).toLocaleDateString();
                mapped.appliedPosition = appData.job?.title || mapped.appliedPosition;
                // attach for later updates
                mapped.applicationId = applicationIdFromState;
              }
            } catch(appErr){
              console.error('Failed to fetch application for candidate detail', appErr);
            }
          }

          setCandidate(mapped);
          setLoading(false);
          return;
        }
      } catch (err) {
        console.error('Failed to fetch candidate', err);
      }

      // Fallback to dummy data if API fails
      const dummyCandidate = {
        id: 1,
        firstName: 'James',
        lastName: 'Wilson',
        email: 'james.wilson@example.com',
        phone: '+1 (555) 123-4567',
        location: 'New York, NY',
        website: 'https://jameswilson.dev',
        position: 'Senior Backend Engineer',
        experience: 7,
        education: [
          {
            degree: 'Master in Computer Science',
            institution: 'New York University',
            year: '2015-2017'
          },
          {
            degree: 'Bachelor in Computer Engineering',
            institution: 'University of Michigan',
            year: '2011-2015'
          }
        ],
        skills: ['Node.js', 'PostgreSQL', 'AWS', 'Docker', 'TypeScript', 'GraphQL', 'Redis', 'CI/CD'],
        languages: [
          { name: 'English', level: 'Native' },
          { name: 'Spanish', level: 'Intermediate' }
        ],
        about: 'Experienced backend engineer with 7+ years of experience building scalable web applications. Passionate about clean code, performance optimization, and cloud architecture.',
        resume: '/uploads/resume-james-wilson.pdf',
        matchScore: 95,
        status: 'Interviewed',
        appliedDate: '2023-09-15',
        appliedPosition: 'Senior Backend Engineer',
        avatar: 'J',
        interviews: [
          {
            id: 1,
            type: 'Technical Interview',
            date: '2023-09-20',
            time: '10:00 AM',
            interviewer: 'Michael Brown',
            status: 'Completed',
            feedback: 'Strong technical skills, especially in database design and API development. Could improve on system design knowledge.',
            rating: 4
          },
          {
            id: 2,
            type: 'Cultural Fit',
            date: '2023-09-25',
            time: '2:00 PM',
            interviewer: 'Sarah Johnson',
            status: 'Completed',
            feedback: 'Great team player with excellent communication skills. Aligned well with company values.',
            rating: 5
          }
        ],
        notes: [
          {
            id: 1,
            author: 'Sarah Johnson',
            date: '2023-09-26',
            content: 'James would be a great fit for the backend team. His experience with our tech stack is impressive.'
          },
          {
            id: 2,
            author: 'Michael Brown',
            date: '2023-09-21',
            content: 'Technical skills are strong. Recommend moving forward with the hiring process.'
          }
        ],
        workAuth: 'US Citizen',
        expectedSalary: {
          min: 100000,
          max: 150000,
          currency: 'USD'
        },
        statusHistory: [],
      };
      setCandidate(dummyCandidate);
      setLoading(false);
    };

    fetchData();
  }, [id]);
  
  // Simple mapper to adapt API user doc to UI-friendly structure
  const mapCandidate = (data) => {
    return {
      id: data._id,
      firstName: data.firstName || '',
      lastName: data.lastName || '',
      email: data.email || '',
      phone: data.phone || '',
      location: data.location || '',
      website: data.socialLinks?.portfolio || data.socialLinks?.linkedin || '',
      position: data.jobPreferences?.preferredJobTitles?.[0] || '',
      experience: data.totalExperience || 0,
      workAuth: data.workAuthorization || data.workAuth || '',
      expectedSalary: data.expectedSalary || {},
      education: data.education || [],
      skills: (data.skills || []).map((s) => (typeof s === 'string' ? s : s.name)),
      languages: data.languages || [],
      about: data.profile?.bio || '',
      resume: data.resume || null,
      matchScore: 0,
      status: 'New',
      appliedDate: '',
      appliedPosition: '',
      avatar: data.firstName ? data.firstName.charAt(0) : 'U',
      interviews: [],
      notes: [],
      statusHistory: [],
    };
  };
  
  // Handle adding a new note
  const handleAddNote = (e) => {
    e.preventDefault();
    
    if (!newNote.trim()) return;
    
    const note = {
      id: candidate.notes.length + 1,
      author: 'You', // In a real app, get from current user
      date: new Date().toISOString().split('T')[0],
      content: newNote
    };
    
    setCandidate(prev => ({
      ...prev,
      notes: [note, ...prev.notes]
    }));
    
    setNewNote('');
    setShowNoteForm(false);
  };
  
  // Update status both locally and via API (if applicationId provided)
  const handleStatusChange = async (newStatus) => {
    setCandidate(prev => ({ ...prev, status: newStatus, statusHistory: [{ status: newStatus, changedAt: new Date().toISOString(), changedBy: 'You' }, ...(prev.statusHistory||[])] }));

    // Try to update backend – applicationId expected in location.state
    try {
      const appId = locationHook.state?.applicationId || candidate.applicationId;
      if (appId) {
        await api.put(`/applications/${appId}/status`, { status: newStatus });
      }
    } catch (err) {
      console.error('Failed to update status', err);
      toast.error('Status update failed on server');
    }

    setShowStatusModal(false);
  };
  
  // View resume helper
  const handleViewResume = async (filename) => {
    if (!filename) {
      toast.error('No resume available for this candidate');
      return;
    }

    try {
      // Use authenticated axios instance to include token automatically
      const res = await api.get(`/upload/files/${filename}`, {
        responseType: 'blob',
      });

      const file = new Blob([res.data], { type: res.headers['content-type'] });
      const url = URL.createObjectURL(file);
      window.open(url, '_blank');
    } catch (err) {
      console.error('Failed to load resume', err);
      toast.error(err.response?.data?.message || 'Unable to load resume');
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="loading-spinner w-8 h-8 border-4"></div>
      </div>
    );
  }
  
  if (!candidate) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-900 font-heading">Candidate not found</h2>
        <p className="mt-1 text-gray-500">
          The candidate you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/candidates" className="mt-4 btn-primary inline-flex">
          Back to Candidates
        </Link>
      </div>
    );
  }
  
  return (
    <motion.div
      className="space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Page header */}
      <motion.div 
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4"
        variants={itemVariants}
      >
        <div>
          <Link to="/candidates" className="text-primary-600 hover:text-primary-700 flex items-center mb-2">
            <ArrowLeft size={16} className="mr-1" />
            Back to Candidates
          </Link>
          <h1 className="page-title">{candidate.firstName} {candidate.lastName}</h1>
          <p className="text-gray-600 mt-1">
            {candidate.position} • Applied on {candidate.appliedDate}
          </p>
        </div>
        
        <div className="flex items-center space-x-3">
          <div className="flex items-center px-3 py-1.5 bg-primary-50 text-primary-700 rounded-lg">
            <Star size={16} className="mr-1.5" fill="currentColor" />
            <span className="font-medium">{candidate.matchScore}% Match</span>
          </div>
          
          {/* Status modal trigger */}
          {showDecisionButtons && (
            <button className="btn-primary" onClick={() => setShowStatusModal(true)}>Change Status</button>
          )}
          
          <button className="btn-outline">
            <Mail size={16} className="mr-2" />
            Contact
          </button>
        </div>
      </motion.div>
      
      {/* Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column - Profile info */}
        <motion.div 
          className="lg:col-span-1"
          variants={itemVariants}
        >
          <div className="bg-white rounded-xl shadow-soft border border-gray-100 overflow-hidden">
            {/* Profile header */}
            <div className="p-6 border-b border-gray-100">
              <div className="flex items-center">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-700 font-medium text-xl">{candidate.avatar}</span>
                </div>
                <div className="ml-4">
                  <h2 className="text-xl font-bold text-gray-900 font-heading">
                    {candidate.firstName} {candidate.lastName}
                  </h2>
                  <p className="text-gray-600">{candidate.position}</p>
                  <div className="mt-1 flex items-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(candidate.status)}`}>
                      {candidate.status}
                    </span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Contact info */}
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Contact Information
              </h3>
              
              <div className="space-y-3">
                <div className="flex items-center">
                  <Mail size={16} className="text-gray-500 mr-3" />
                  <a href={`mailto:${candidate.email}`} className="text-gray-800 hover:text-primary-600">
                    {candidate.email}
                  </a>
                </div>
                {candidate.workAuth && (
                  <div className="flex items-center mt-2">
                    <Shield size={16} className="text-gray-500 mr-3" />
                    <span className="text-gray-800">{candidate.workAuth}</span>
                  </div>
                )}
                
                <div className="flex items-center">
                  <Phone size={16} className="text-gray-500 mr-3" />
                  <a href={`tel:${candidate.phone}`} className="text-gray-800 hover:text-primary-600">
                    {candidate.phone}
                  </a>
                </div>
                
                <div className="flex items-center">
                  <MapPin size={16} className="text-gray-500 mr-3" />
                  <span className="text-gray-800">{candidate.location}</span>
                </div>
                
                {candidate.website && (
                  <div className="flex items-center">
                    <Globe size={16} className="text-gray-500 mr-3" />
                    <a 
                      href={candidate.website} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-gray-800 hover:text-primary-600 flex items-center"
                    >
                      Personal Website
                      <ExternalLink size={12} className="ml-1" />
                    </a>
                  </div>
                )}
              </div>
            </div>
            
            {/* Skills */}
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Skills
              </h3>
              
              <div className="flex flex-wrap gap-2">
                {candidate.skills.map((skill, index) => (
                  <span 
                    key={index}
                    className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-gray-100 text-gray-800"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>
            
            {/* Languages */}
            <div className="p-6 border-b border-gray-100">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Languages
              </h3>
              
              <div className="space-y-2">
                {candidate.languages.map((language, index) => (
                  <div key={index} className="flex justify-between items-center">
                    <span className="text-gray-800">{language.name}</span>
                    <span className="text-sm text-gray-500">{language.level}</span>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Resume */}
            <div className="p-6">
              <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-4">
                Documents
              </h3>
              
              {candidate.resume ? (
                <button
                  type="button"
                  onClick={() => handleViewResume(candidate.resume.filename)}
                  className="w-full text-left flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                    <FileText size={20} className="text-primary-600" />
                  </div>
                  <div className="ml-3 flex-1 overflow-hidden">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {candidate.resume.originalName || 'Resume'}
                    </p>
                    <p className="text-xs text-gray-500 truncate">
                      {candidate.resume.size ? `${(candidate.resume.size / (1024*1024)).toFixed(1)} MB` : ''}
                    </p>
                  </div>
                  <Download size={16} className="text-gray-500 flex-shrink-0" />
                </button>
              ) : (
                <p className="text-sm text-gray-500">No resume available</p>
              )}
            </div>
          </div>
        </motion.div>
        
        {/* Right column - Tabs content */}
        <motion.div 
          className="lg:col-span-2"
          variants={itemVariants}
        >
          {/* Tabs */}
          <div className="bg-white rounded-xl shadow-soft border border-gray-100 overflow-hidden">
            <div className="border-b border-gray-100">
              <nav className="flex" aria-label="Tabs">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`px-4 py-4 text-sm font-medium ${
                    activeTab === 'profile'
                      ? 'border-b-2 border-primary-600 text-primary-600'
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Profile
                </button>
               
               
              </nav>
            </div>
            
            <div className="p-6">
              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div className="space-y-6">
                  {/* About */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">About</h3>
                    <p className="text-gray-700 whitespace-pre-line">{candidate.about}</p>
                  </div>
                  
                  {/* Experience */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Experience</h3>
                    <div className="flex items-center">
                      <Briefcase size={18} className="text-gray-500 mr-2" />
                      <span className="text-gray-700">{candidate.experience} years of experience</span>
                    </div>
                    {candidate.expectedSalary?.min && (
                      <div className="flex items-center mt-2 text-sm text-gray-600">
                        Expected Salary: {candidate.expectedSalary.min} - {candidate.expectedSalary.max} {candidate.expectedSalary.currency || 'USD'}
                      </div>
                    )}
                  </div>
                  
                  {/* Education */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Education</h3>
                    <div className="space-y-4">
                      {candidate.education.map((edu, index) => (
                        <div key={index} className="flex">
                          <div className="mr-4">
                            <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
                              <Award size={20} className="text-primary-600" />
                            </div>
                          </div>
                          <div>
                            <h4 className="text-base font-medium text-gray-900">{edu.degree}</h4>
                            <p className="text-gray-600">{edu.institution}</p>
                            <p className="text-sm text-gray-500">{edu.year}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  {/* Application Details */}
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-3">Application Details</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-gray-500">Applied Position</p>
                          <p className="font-medium text-gray-900">{candidate.appliedPosition}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-500">Application Date</p>
                          <div className="flex items-center">
                            <Calendar size={16} className="text-gray-500 mr-2" />
                            <p className="font-medium text-gray-900">{candidate.appliedDate}</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Interviews Tab */}
              {activeTab === 'interviews' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">Interview History</h3>
                    <button className="btn-primary">
                      Schedule Interview
                    </button>
                  </div>
                  
                  {candidate.interviews.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No interviews scheduled yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {candidate.interviews.map((interview) => (
                        <div 
                          key={interview.id}
                          className="border border-gray-200 rounded-lg p-4"
                        >
                          <div className="flex justify-between">
                            <div>
                              <h4 className="font-medium text-gray-900">{interview.type}</h4>
                              <div className="flex items-center mt-1">
                                <Calendar size={16} className="text-gray-500 mr-2" />
                                <span className="text-gray-700">{interview.date}, {interview.time}</span>
                              </div>
                              <p className="text-sm text-gray-600 mt-1">
                                Interviewer: {interview.interviewer}
                              </p>
                            </div>
                            <div className="text-right">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                interview.status === 'Completed' 
                                  ? 'bg-success-100 text-success-800' 
                                  : 'bg-warning-100 text-warning-800'
                              }`}>
                                {interview.status}
                              </span>
                              
                              {interview.status === 'Completed' && (
                                <div className="mt-2 flex justify-end">
                                  {[...Array(5)].map((_, i) => (
                                    <Star 
                                      key={i}
                                      size={16} 
                                      className={i < interview.rating ? 'text-warning-500' : 'text-gray-300'} 
                                      fill={i < interview.rating ? 'currentColor' : 'none'}
                                    />
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                          
                          {interview.feedback && (
                            <div className="mt-3 pt-3 border-t border-gray-100">
                              <p className="text-sm text-gray-500 font-medium">Feedback:</p>
                              <p className="text-gray-700 mt-1">{interview.feedback}</p>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              
              {/* Notes Tab */}
              {activeTab === 'notes' && (
                <div className="space-y-6">
                  <div className="flex justify-between items-center">
                    <h3 className="text-lg font-medium text-gray-900">Notes</h3>
                    <button 
                      className="btn-primary"
                      onClick={() => setShowNoteForm(true)}
                    >
                      Add Note
                    </button>
                  </div>
                  
                  {/* Add note form */}
                  {showNoteForm && (
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <form onSubmit={handleAddNote}>
                        <textarea
                          value={newNote}
                          onChange={(e) => setNewNote(e.target.value)}
                          placeholder="Write your note here..."
                          className="input-field w-full"
                          rows={3}
                        />
                        <div className="flex justify-end mt-3 space-x-2">
                          <button 
                            type="button"
                            className="btn-secondary"
                            onClick={() => {
                              setShowNoteForm(false);
                              setNewNote('');
                            }}
                          >
                            Cancel
                          </button>
                          <button 
                            type="submit"
                            className="btn-primary"
                          >
                            Add Note
                          </button>
                        </div>
                      </form>
                    </div>
                  )}
                  
                  {candidate.notes.length === 0 ? (
                    <div className="text-center py-8">
                      <p className="text-gray-500">No notes added yet.</p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {candidate.notes.map((note) => (
                        <div 
                          key={note.id}
                          className="border border-gray-200 rounded-lg p-4"
                        >
                          <div className="flex justify-between">
                            <div className="flex items-center">
                              <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center">
                                <User size={16} className="text-primary-600" />
                              </div>
                              <span className="ml-2 font-medium text-gray-900">{note.author}</span>
                            </div>
                            <span className="text-sm text-gray-500">{note.date}</span>
                          </div>
                          <p className="mt-3 text-gray-700">{note.content}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Action buttons */}
        
        </motion.div>
      </div>

      {/* Status Modal */}
      {showDecisionButtons && showStatusModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-elegant w-full max-w-lg max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-center p-4 border-b border-gray-100">
              <h3 className="text-lg font-semibold">Update Candidate Status</h3>
              <button onClick={()=>setShowStatusModal(false)} className="text-gray-400 hover:text-gray-600"><X size={18}/></button>
            </div>

            <div className="p-4 space-y-6">
              {/* Stepper */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Progress</h4>
                <ol className="relative border-l border-gray-200">
                  {STATUS_FLOW.map((step) => {
                    const currentIdx = STATUS_FLOW.indexOf(candidate.status);
                    const stepIdx = STATUS_FLOW.indexOf(step);
                    const reached = stepIdx <= currentIdx;
                    return (
                      <li key={step} className="ml-4 mb-4">
                        <div className={`w-3 h-3 rounded-full ${reached ? 'bg-primary-500' : 'bg-gray-300'} absolute -left-1.5 top-1.5`}></div>
                        <span className={`text-sm ${reached ? 'text-gray-900' : 'text-gray-500'}`}>{step}</span>
                      </li>
                    );
                  })}
                </ol>
              </div>

              {/* Next action buttons */}
              <div className="space-y-3">
                {(() => {
                  const next = getNextStatus(candidate.status);
                  return next ? (
                    <button className="btn-primary w-full" onClick={()=>handleStatusChange(next)}>
                      Move to {next}
                    </button>
                  ) : null;
                })()}
                <button className="btn-secondary w-full" onClick={()=>handleStatusChange('Rejected')}>
                  Mark as Rejected
                </button>
              </div>

              {/* History */}
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">History</h4>
                {candidate.statusHistory && candidate.statusHistory.length>0 ? (
                  <ul className="space-y-2 text-sm">
                    {candidate.statusHistory.map((h,i)=>(
                      <li key={i} className="flex justify-between">
                        <span>{h.status}</span>
                        <span className="text-gray-500">{new Date(h.changedAt).toLocaleDateString()}</span>
                      </li>
                    ))}
                  </ul>
                ):<p className="text-gray-500 text-sm">No history yet</p>}
              </div>
            </div>
          </div>
        </div>
      )}
    </motion.div>
  );
};

export default CandidateDetail; 